import { fetcher } from "@/lib/api/fetcher";
import { environments } from "@/lib/config";
import { MediaFormData } from "@/modules/product-management/templates/media-form";
import { MediaProductFormData } from "@/modules/product-management/templates/media-product-form";
import { ResponseData } from "@/types/common";
import {
  MediaDataResponse,
  MediaCategoryDataRes,
  PreviousMediaSensorInfo,
  Media,
  MediaProductCountStatus,
  LocationType,
} from "@/types/media";
import {
  MediaProductByOrderDetailType,
  MediaProductDataType,
  UpdateMultipleStatusPayload,
} from "@/types/media-product";

const apiUrl = environments.restApiUrl;

export type CreateMediaPayload = MediaFormData;
export type CreateMediaProductPayload = MediaProductFormData;
export type UpdateMediaProductPayload =
  | MediaProductFormData
  | { status: string; is_draft: boolean };
export type CountStatusParamType =
  | "media_o"
  | "media_product"
  | "contract_advertiser_mine"
  | "contract_advertiser_team"
  | "contract_media"
  | "contract_platform"
  | "media_in_contract";

export type DeleteMediaPayload = {
  media_ids: string[];
};

type DeleteMediaProductPayload = {
  media_product_ids: string[];
};

export const getMediaList = (params: Record<string, string>) => {
  return fetcher<ResponseData<MediaDataResponse>>(`${apiUrl}/media`, {
    queryParams: params,
  });
};

export const getMediaDetail = (id: number) => {
  return fetcher<{ data: Media }>(`${apiUrl}/media/${id}`);
};

export const getCountStatus = <T>(type: CountStatusParamType) => {
  return fetcher<ResponseData<T>>(`${apiUrl}/common/count-status?type=${type}`);
};

export const getMediaLinked = <T>(params: Record<string, string>) => {
  return fetcher<ResponseData<T>>(`${apiUrl}/media-cms/list-media-mapper`, {
    queryParams: params,
  });
};

export const createMedia = (body: CreateMediaPayload) => {
  return fetcher(`${apiUrl}/media`, { method: "POST", body });
};

export const updateMedia = (id: string, body: CreateMediaPayload) => {
  delete body.status;
  return fetcher(`${apiUrl}/media/${id}`, {
    method: "PATCH",
    body,
  });
};

export const deleteMedia = (payload: DeleteMediaPayload) => {
  return fetcher(`${apiUrl}/media/delete`, {
    method: "DELETE",
    body: payload,
  });
};

export const fetchMediaCategories = () => {
  return fetcher<ResponseData<MediaCategoryDataRes[]>>(
    `${apiUrl}/media/get-media-category`
  );
};

export const getMediaCategoriesByStatus = () => {
  return fetcher<ResponseData<MediaCategoryDataRes[]>>(
    `${apiUrl}/media/get-media-category-by-status`
  );
}

export const getMediaProductList = (params: Record<string, string>) => {
  return fetcher<ResponseData<{
    media_product_list: MediaProductDataType[],
    total_status: MediaProductCountStatus['summary']
  }>>(
    `${apiUrl}/media-product/all`,
    {
      queryParams: params,
    }
  );
};

export const createMediaProduct = (body: CreateMediaProductPayload) => {
  return fetcher<ResponseData<MediaDataResponse[]>>(
    `${apiUrl}/media-product/create`,
    { method: "POST", body }
  );
};

export const updateMediaProduct = (
  id: number,
  body: UpdateMediaProductPayload | { status: string }
) => {
  return fetcher<ResponseData<MediaDataResponse[]>>(
    `${apiUrl}/media-product/${id}/update`,
    { method: "PATCH", body }
  );
};

export const getMediaProductDetail = (id: string) => {
  return fetcher<ResponseData<MediaProductDataType>>(
    `${apiUrl}/media-product/${id}`
  );
};

// 연동대기(미연동) 항목 상세보기 — CMS 미디어그룹 코드로 조회 (media_product_id 없음)
export const getMediaProductPendingDetail = (mediaGroupCd: string) => {
  return fetcher<ResponseData<MediaProductDataType>>(
    `${apiUrl}/media-product/pending/${mediaGroupCd}`
  );
};

export const deleteMediaProduct = (ids: DeleteMediaProductPayload) => {
  return fetcher(`${apiUrl}/media-product/delete`, {
    method: "DELETE",
    body: ids,
  });
};

export const updateMultipleStatus = (body: UpdateMultipleStatusPayload) => {
  return fetcher(`${apiUrl}/media-product/update-multiple-status`, {
    method: "PATCH",
    body,
  });
};

export const getPreviousMediaSensorInfo = () => {
  return fetcher<PreviousMediaSensorInfo>(`${apiUrl}/media/is-save-next-time`);
};

export const getMediaProductByOrderDetailIds = (id: number) => {
  return fetcher<ResponseData<MediaProductByOrderDetailType>>(
    `${apiUrl}/media-product/order-detail/${id}/product-info`
  );
};

export const getGeo = (parent_code: string | null) => {
  const url = parent_code
    ? `${apiUrl}/geo?parent_code=${parent_code}`
    : `${apiUrl}/geo`;

  return fetcher<ResponseData<LocationType[]>>(url);
};

